import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useUnitChainData } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { Layout } from 'modules/layout';

import { useGetApiTaskQuery } from '../actions/getApiTask';
import { useGetTaskQuery } from '../actions/getTask';
import { Executions } from '../components/Executions';
import { TaskPage } from '../components/TaskPage';
import { TaskView } from '../components/TaskView';
import { TASK_CONTENT_MAX_WIDTH } from '../consts';
import translation from './editTask.translation';

export function EditTask(): JSX.Element {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);
  const { search } = useLocation();

  const { taskId, chainUnit } = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return {
      chainUnit: searchParams.get('network') ?? '',
      taskId: searchParams.get('taskId') ?? '',
    };
  }, [search]);
  const chainData = useUnitChainData(chainUnit);
  const chainId = chainData?.chainId ?? null;

  const { data } = useGetTaskQuery({
    taskId,
    chainId,
  });
  const { data: apiData } = useGetApiTaskQuery({
    taskId,
    chainId,
  });

  return (
    <Layout>
      <div className={classes.container}>
        <TaskPage title={apiData?.name ?? t(keys.title)}>
          {data && (
            <TaskView
              taskId={data.taskId}
              chainId={data.chainId}
              isPaused={data.isPaused}
              funcName={apiData?.funcName}
              params={apiData?.params}
              cronTarget={apiData?.cronTarget}
              cronDate={apiData?.cronDate}
              amountSpent={data.amountSpent}
            />
          )}
        </TaskPage>

        {taskId && data?.chainId && (
          <Paper className={classes.paper}>
            <Executions taskId={taskId} chainId={data.chainId} />
          </Paper>
        )}
      </div>
    </Layout>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(10, 0),
    gap: theme.spacing(10),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(8, 0),
      gap: theme.spacing(6),
    },
  },
  paper: {
    padding: theme.spacing(12),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: TASK_CONTENT_MAX_WIDTH,
    margin: '0 auto',

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(8, 4),
    },
  },
}));
